<template>
  <div>
    <panel class="pt-6">
      <h3 class="mb-2 uppercase">{{ selectedOffice.network_id === 2 ? 'Marketing Packs' : 'Enhanced Marketing Packs' }}</h3>
      <div class="flex flex-wrap -mx-3">
        <div class="w-full md:w-1/2 lg:w-1/4 px-3 mb-6">
          <label class="uppercase text-grey-darker mb-1 block">Status</label>
          <select v-model="searchParams.status" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              <option v-for="(option, j) in statusOptions" :key="j" :value="option.id">
                {{ option.value }}
              </option>
          </select>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/4 px-3 mb-6">
          <label class="uppercase text-grey-darker mb-1 block">Search</label>
          <input type="text" v-model="searchParams.q" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
        </div>
      </div>

      <hr class="border-b border-grey-darker w-full mb-6" />

      <loader v-if="orders === null" />
      <div v-else-if="orders.data.length === 0">
        <strong class="mb-2">No Orders</strong>
        <p>Your search returned no results</p>
      </div>
      <div v-else>
        <table class="table-auto text-center w-full">
          <thead class="uppercase text-xs">
            <tr>
              <th class="p-2">#</th>
              <th class="text-left p-2 whitespace-no-wrap">
                Property
              </th>
              <th class="p-2 whitespace-no-wrap">
                Package
              </th>
              <th class="p-2 whitespace-no-wrap">
                Order Placed
              </th>
              <th class="p-2 whitespace-no-wrap">
                Status
              </th>
              <th class="p-2 whitespace-no-wrap">
                Media
              </th>
              <th class="p-2 whitespace-no-wrap">
                Interview
              </th>
              <th class="p-2 whitespace-no-wrap">
                Artwork
              </th>
              <th class="p-2 whitespace-no-wrap">
                Full Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in orders.data" :key="order.id" class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer" @click="$emit('select-order', order.id)">
              <td class="px-2 py-4 border-l-4" :class="borderForStatus(order.status)">{{ order.id }}</td>
              <td class="px-2 py-4 text-left">
                {{ order.address }}
              </td>
              <td class="px-2 py-4">
                {{ order.pack.name }}
                <small class="text-grey-darker block">{{ order.pack.delivery_type }} Delivery</small>
              </td>
              <td class="px-2 py-4">
                {{ order.order_placed | date }}
                <small class="text-grey-darker block">{{ order.order_placed | time }}</small>
              </td>
              <td class="px-2 py-4" :class="textForStatus(order.status)">
                <i class="fas fa-circle"></i> {{ order.status }}
              </td>
              <td class="px-2 py-4" :class="order.source_material.status === 'Complete' ? 'text-green-dark' : ''">
                {{ order.source_material.status }}
                <small class="text-grey-darker block" v-if="order.source_material.date">{{ order.source_material.date | datetime(false) }}</small>
              </td>
              <td class="px-2 py-4" :class="order.interview && order.interview.status === 'Complete' ? 'text-green-dark' : ''">
                {{ order.interview ? order.interview.status : 'N/A' }}
                <small class="text-grey-darker block" v-if="order.interview && order.interview.date">{{ order.interview.date | datetime(false) }}</small>
              </td>
              <td class="px-2 py-4" :class="order.artwork.status === 'Complete' ? 'text-green-dark' : ''">
                {{ order.artwork.status }}
                <small class="text-grey-darker block" v-if="order.artwork.date">{{ order.artwork.date | datetime(false) }}</small>
              </td>
              <td class="px-2 py-4 border-r-4" :class="borderForStatus(order.status)">
                <i class="fas fa-tasks text-lg"></i>
              </td>
            </tr>
          </tbody>
        </table>

        <paginator :data="orders.meta" @change="pageChanged" />
      </div>
    </panel>
  </div>
</template>

<script>
import api from '@/http'
import debounce from 'lodash/debounce'
import { mapState } from 'vuex'
export default {
  data () {
    return {
      searchParams: {
        page: 1,
        q: '',
        status: null
      },
      pageHasChanged: false,
      orders: null,
      statusOptions: [
        { id: null, value: 'All' },
        { id: 4, value: 'Pending Source Material' },
        { id: 6, value: 'Pending Artwork' },
        { id: 8, value: 'Complete' },
        { id: 'cancelled', value: 'Cancelled' }
      ]
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  watch: {
    searchParams: {
      handler () {
        this.search()
      },
      deep: true
    },
    selectedOffice: {
      handler () {
        this.orders = null
        this.search()
      },
      deep: true
    }
  },
  created () {
    this.search()
  },
  methods: {
    search: debounce(function () {
      if (!this.selectedOffice) {
        return
      }
      this.searchParams.office_id = this.selectedOffice.id

      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      api.get('/marketing-packs/orders', {
        params: this.searchParams
      }).then((response) => {
        this.orders = response.data
      })
    }, 500),
    borderForStatus (status) {
      switch (status) {
        case 'Pending Source Material':
        case 'Pending Source Material Vendor Approval':
        case 'Pending Artwork Approval':
        case 'Cancelled':
          return 'border-red-dark'
        case 'Complete':
          return 'border-green-dark'
        default:
          return 'border-yellow-dark'
      }
    },
    textForStatus (status) {
      switch (status) {
        case 'Pending Source Material':
        case 'Pending Source Material Vendor Approval':
        case 'Pending Artwork Approval':
        case 'Cancelled':
          return 'text-red-dark'
        case 'Complete':
          return 'text-green-dark'
        default:
          return 'text-yellow-dark'
      }
    },
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    }
  }
}
</script>
